/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { AppService } from '@services/app/app.service';
import { HttpClient } from '@angular/common/http';
import { LabelItemDTO } from '@api/models/label-item.dto';
import { mergeMap, map } from 'rxjs/operators';
import { ApiService } from '@api/services/api/api.service';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import KeyCutter from './key-cutter';
import { AdvancedLabelModel } from '@core/models';

@Injectable({
  providedIn: 'root'
})
export class LabelsService {
  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private appService: AppService,
    private translate: TranslateService
  ) {}

  /**
   * carica le labels pubbliche
   */
  loadOpenLabels(): Observable<void> {
    const url = this.appService.getConfig().labels.openLabelsUrl;

    if (!url) {
      return of([]).pipe(map(() => {}));
    }

    return this.http.get<LabelItemDTO[]>(url).pipe(
      map(res => this.listToMap(res || [])),
      mergeMap(labels => this.setTranslation(labels)),
      map(() => {})
    );
  }

  /**
   * carica le labels
   */
  loadLabels(): Observable<void> {
    return this.apiService.getLabels({}).pipe(
      map(res => this.listToMap(res.labels || [])),
      mergeMap(labels => this.setTranslation(labels)),
      map(() => {})
    );
  }

  /**
   * restituisce traduzione per la key richiesta;
   * assicurarsi di usare il metodo quando le label sono caricate
   */
  getLabel(
    key: string,
    options: {
      interpolateParams?: { [key: string]: string };
    } = {}
  ): string {
    return this.translate.instant(key, options.interpolateParams);
  }

  /**
   * controlla se la label richiesta esiste
   */
  doesLabelExists(key: string): boolean {
    return this.getLabel(key) !== key;
  }

  /**
   * imposta le traduzioni in base alle label caricate
   */
  private setTranslation(labelsObj: {
    [key: string]: string;
  }): Observable<boolean> {
    const lang = this.getUserLang();

    this.translate.setTranslation(lang, labelsObj, true);
    this.translate.setDefaultLang(lang);

    return this.translate.use(lang).pipe(map(() => true));
  }

  /**
   * converte la lista di label in una mappa
   */
  private listToMap(list: LabelItemDTO[]): { [key: string]: string } {
    const labelMap = {};

    if (!Array.isArray(list)) {
      return labelMap;
    }

    list
      .map(label => this.getAdvancedLabel(label))
      .filter(item => this.isQueryFilterPassed(item.filter))
      // ordino in modo da avere all'inizio le label senza filtro
      // in modo che alla fine venga sempra conservata l'eventuale
      // lable con filtro
      .sort((a, b) => {
        if (a.filter === b.filter) {
          return a.part - b.part;
        } else {
          return a.filter < b.filter ? -1 : 1;
        }
      })
      .forEach(item => {
        if (item.part === 1) {
          labelMap[item.key] = item.value;
        } else {
          labelMap[item.key] += item.value;
        }
      });

    return labelMap;
  }

  /**
   * crea oggetto label avanzato con indicazioni sul filtro e la parte
   */
  private getAdvancedLabel(label: LabelItemDTO): AdvancedLabelModel {
    const filterAttrKey = this.getQueryFilterKey();
    const labelKey = new KeyCutter(label.key);

    return {
      filter: labelKey.getAttribute(filterAttrKey) || '',
      part: Number(labelKey.getAttribute('pt') || '1'),
      key: labelKey.getClean(),
      value: label.value
    };
  }

  /**
   * stabilisce se il valore è compatibile con il query filter
   */
  private isQueryFilterPassed(value: string): boolean {
    const filterAttrValue = this.getQueryFilterValue();

    return !filterAttrValue || !value || value === filterAttrValue;
  }

  /**
   * restituisce la chiave del filtro
   */
  private getQueryFilterKey(): string {
    const filter = this.appService.getConfig().labels.filter;
    return filter && filter.key;
  }

  /**
   * restituisce il valore del filtro
   */
  private getQueryFilterValue(): string {
    const filter = this.appService.getConfig().labels.filter;
    return filter && filter.value;
  }

  /**
   * definisce lal lingua impostata dall'utente
   */
  private getUserLang(): string {
    return this.translate.getBrowserCultureLang();
  }
}
